import { formatDateTime } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

import type { CurrencyBreakdown, MethodKind } from './shiftBreakdown.js';
import { overIsNonCash, type DrawerCard, type ShiftSaleRow } from './ShiftView.js';

/**
 * The shift close-out slip, printed from the office — for a till (.exe)
 * close-out and a web-register shift alike, from the same model the shift
 * dialog draws (shiftBreakdown.ts), so the paper and the screen never disagree.
 *
 * 80mm like the till's own slip, and with everything on it: the takings per
 * currency split by how they were paid (cash, card, transfer, on account…),
 * returns, customers' debt payments, each currency's drawer, and the lines
 * behind every non-cash figure — the card sales to tick off against the card
 * machine, the sales on account, the returns and the debt payments.
 *
 * A pure string function you can read end to end; printed through a hidden
 * iframe (printHtml) so no pop-up blocker eats it.
 */

/** Both renderers' fixed print palette. The app's own tokens are irrelevant on paper. */
/* eslint-disable no-restricted-syntax */
const INK = '#17181c';
const MUTED = '#6b7280';
const RULE = '#cfd3da';
const PANEL = '#F1F3F7';
/* eslint-enable no-restricted-syntax */

export interface ShiftPrintModel {
  /** SHIFT-000014 for a register shift; none for a till slip. */
  number?: string | null;
  cashier?: string | null;
  device?: string | null;
  openedAt: string;
  /** null while the shift is still open. */
  closedAt: string | null;
  salesCount: number;
  shiftCurrency: string;
  breakdown: CurrencyBreakdown[];
  drawers: DrawerCard[];
  note?: string | null;
  /** The shift's sales list — its non-cash lines, returns and debt payments are printed. */
  rows?: ShiftSaleRow[] | null;
}

type T = (key: string, opts?: Record<string, unknown>) => string;

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

/**
 * Print without a pop-up.
 *
 * The till does it this way and the office should too: `window.open` is what a
 * pop-up blocker eats, and a print that silently does nothing is worse than one
 * that fails loudly. A hidden iframe is same-document, so it always prints — and
 * it is removed afterwards rather than left accumulating in the DOM.
 */
export function printHtml(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) { iframe.remove(); return; }
  doc.open();
  doc.write(html);
  doc.close();
  // A tick for the iframe to lay out before printing, then a delayed removal:
  // tearing it down synchronously after print() cancels the job in some browsers.
  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      /* print blocked — nothing useful to do, and throwing would break the page */
    }
    setTimeout(() => iframe.remove(), 1500);
  }, 250);
}

/** A method's name on paper and on screen. */
export function methodLabel(t: T, kind: string): string {
  return t(`shifts.${kind}`, { defaultValue: kind });
}

export function buildShiftPrintHtml(
  m: ShiftPrintModel,
  t: T,
  opts: {
    storeName: string;
    locale: string;
    rtl: boolean;
    /** Today's rates FROM the shift currency (1 base = rate × code) — the overall line only. */
    rates?: Record<string, number>;
  },
): string {
  const dir = opts.rtl ? 'rtl' : 'ltr';
  // Every amount sits in its own LTR run on the slip; the direction marks Arabic
  // number formatting adds would otherwise move a sign to the far side.
  const mc = (v: string | number, ccy: string): string =>
    formatMoney(String(v), { locale: opts.locale, currency: ccy }).replace(/[\u200e\u200f]/g, '');
  const when = (iso: string): string => formatDateTime(iso, { locale: opts.locale });
  const val = (text: string): string => `<span class="val" dir="ltr">${esc(text)}</span>`;
  const row = (lab: string, text: string, cls = ''): string =>
    `<div class="row ${cls}"><span class="lab">${esc(lab)}</span>${val(text)}</div>`;
  const sec = (title: string, right = ''): string =>
    `<div class="sec"><span>${esc(title)}</span>${right ? `<span dir="ltr">${esc(right)}</span>` : ''}</div>`;
  const positive = (v: string | null | undefined): v is string => v != null && Number(v) > 0.004;
  const counted = (label: string, n: number | null): string => (n === null ? label : `${label} (${n})`);
  const open = m.closedAt === null;

  const minutes = Math.max(0, Math.round(((m.closedAt ? new Date(m.closedAt).getTime() : Date.now()) - new Date(m.openedAt).getTime()) / 60000));
  const duration = t('shifts.duration', { defaultValue: '{{h}} h {{m}} min', h: Math.floor(minutes / 60), m: minutes % 60 });

  // ---- sales, by currency and how they were paid
  const salesHtml = m.breakdown.map((c) => {
    const lines = [row(t('shifts.salesIn', { defaultValue: '{{currency}} sales', currency: c.currencyCode }), mc(c.takings, c.currencyCode), 'big')];
    for (const x of c.methods) {
      const share = Number(c.takings) > 0 ? Math.round((Number(x.total) / Number(c.takings)) * 100) : null;
      lines.push(row(
        `${counted(methodLabel(t, x.kind), x.count)}${share !== null && share > 0 && share < 100 ? ` · ${share}%` : ''}`,
        mc(x.total, c.currencyCode), 'sub',
      ));
    }
    if (!c.methodsKnown) lines.push(`<div class="hint">${esc(t('shifts.methodsNotReported', { defaultValue: 'This till did not report how these were paid.' }))}</div>`);
    if (c.returns) {
      lines.push(row(t('shifts.returnsRow', { defaultValue: 'Returns ({{n}})', n: c.returns.count }), `− ${mc(c.returns.total, c.currencyCode)}`, 'sub neg'));
    }
    return lines.join('');
  }).join('');

  // ---- customers paying off their debt
  const payBlocks = m.breakdown.filter((c) => c.payments.length > 0);
  const paymentsHtml = payBlocks.length === 0 ? '' : sec(t('shifts.debtPayments', { defaultValue: 'Debt payments' }))
    + payBlocks.map((c) => c.payments.map((p) =>
      row(`${counted(methodLabel(t, p.method), p.count)} · ${c.currencyCode}`, mc(p.total, c.currencyCode))).join('')).join('');

  // ---- each currency's drawer
  const result = (d: number, ccy: string): string => {
    const label = Math.abs(d) < 0.005
      ? t('shifts.balanced', { defaultValue: 'Balanced' })
      : d > 0 ? t('shifts.over', { defaultValue: 'Over' }) : t('shifts.short', { defaultValue: 'Short' });
    const tone = Math.abs(d) < 0.005 ? 'ok' : d > 0 ? 'over' : 'short';
    const amount = Math.abs(d) < 0.005 ? '✓' : `${d > 0 ? '+' : '−'}${mc(Math.abs(d), ccy)}`;
    return `<div class="diff ${tone}"><span>${esc(label)}</span><span dir="ltr">${esc(amount)}</span></div>`;
  };
  const drawersHtml = m.drawers.map((c) => {
    const mf = (v: string | number): string => mc(v, c.currencyCode);
    return [
      sec(t('shifts.drawer', { defaultValue: 'Drawer · {{currency}}', currency: c.currencyCode })),
      c.float !== null ? row(t('shifts.float', { defaultValue: 'Opening float' }), mf(c.float)) : '',
      positive(c.nonCash) ? row(t('shifts.notInDrawer', { defaultValue: 'Card / transfer / on account (not in the drawer)' }), mf(c.nonCash), 'sub') : '',
      row(t('shifts.received', { defaultValue: 'Cash received' }), mf(c.received)),
      positive(c.fromOtherSales) ? row(t('shifts.fromOtherSales', { defaultValue: 'of which for sales in other currencies' }), mf(c.fromOtherSales), 'sub') : '',
      positive(c.collected) ? row(t('shifts.collected', { defaultValue: 'of which customers paying their debt' }), mf(c.collected), 'sub') : '',
      positive(c.paidInOtherNotes)
        ? row(t('shifts.paidInOtherNotes', { defaultValue: '{{currency}} sales paid in other notes', currency: c.currencyCode }), mf(c.paidInOtherNotes), 'sub') : '',
      positive(c.changeGiven) ? row(t('shifts.changeGiven', { defaultValue: 'Change given' }), `− ${mf(c.changeGiven)}`) : '',
      positive(c.paidOut)
        ? row(c.paidOutLabel === 'refunds'
          ? t('shifts.refunds', { defaultValue: 'Cash refunds' })
          : t('shifts.paidOut', { defaultValue: 'Change & refunds paid out' }), `− ${mf(c.paidOut)}`)
        : '',
      row(t('shifts.expected', { defaultValue: 'Expected cash' }), mf(c.expected), 'big line'),
      c.counted === null
        ? `<div class="diff">${esc(t('shifts.stillOpen', { defaultValue: 'Still open — not counted yet' }))}</div>`
        : row(t('shifts.counted', { defaultValue: 'Counted' }), mf(c.counted)) + (c.difference === null ? '' : result(c.difference, c.currencyCode)),
      overIsNonCash(c)
        ? `<div class="hint">⚠ ${esc(t('shifts.overIsNonCash', { defaultValue: 'The over equals the card / transfer / on-account sales — were cash sales rung up as card?' }))}</div>`
        : '',
      positive(c.collectedOther)
        ? `<div class="hint">${esc(t('shifts.collectedOther', { defaultValue: 'Debt paid by card / other (not in the drawer)' }))}: <span dir="ltr">${esc(mf(c.collectedOther))}</span></div>`
        : '',
    ].join('');
  }).join('');

  // Overall in the shift currency at today's rate — only when two or more
  // currencies are off and each has a rate. A cashier who swaps dollars for
  // dinars in the drawer reads "over" in one and "short" in the other; this is
  // the line that says what really happened.
  const offs = m.drawers.filter((c) => c.difference !== null && Math.abs(c.difference) > 0.004);
  const rates = opts.rates ?? {};
  const overall = !open && offs.length > 1 && offs.every((c) => c.currencyCode === m.shiftCurrency || rates[c.currencyCode])
    ? offs.reduce((acc, c) => acc + (c.currencyCode === m.shiftCurrency ? c.difference! : c.difference! / rates[c.currencyCode]!), 0)
    : null;

  // ---- the lines behind each non-cash figure
  const rows = (m.rows ?? []).filter((r) => r.kind !== 'voided');
  const group = (title: string, list: ShiftSaleRow[], neg = false): string => {
    if (list.length === 0) return '';
    const byCcy = new Map<string, number>();
    for (const r of list) byCcy.set(r.currencyCode, (byCcy.get(r.currencyCode) ?? 0) + Number(r.total ?? 0));
    const totals = [...byCcy.entries()].map(([ccy, v]) => mc(v, ccy)).join(' · ');
    return `<div class="gh"><span>${esc(title)} (${list.length})</span><span dir="ltr">${esc(neg ? `− ${totals}` : totals)}</span></div>`
      + list.map((r) => `<div class="ln"><span class="who">${esc(r.number)}${r.party ? ` · ${esc(r.party)}` : ''}${r.paidWith ? ` · ${esc(r.paidWith)}` : ''}</span>`
        + `<span dir="ltr" class="amt">${esc(`${neg ? '− ' : ''}${mc(r.total ?? 0, r.currencyCode)}`)}</span></div>`).join('');
  };
  const sales = rows.filter((r) => r.kind === 'sale');
  const withKind = (k: MethodKind[]): ShiftSaleRow[] => sales.filter((r) => (r.methodKinds ?? []).some((x) => k.includes(x)));
  const detailHtml = [
    group(t('shifts.linesFor', { defaultValue: '{{method}} sales', method: methodLabel(t, 'card') }), withKind(['card'])),
    group(t('shifts.linesFor', { defaultValue: '{{method}} sales', method: methodLabel(t, 'transfer') }), withKind(['transfer', 'wallet'])),
    group(t('shifts.linesFor', { defaultValue: '{{method}} sales', method: methodLabel(t, 'other') }), withKind(['other', 'cashback'])),
    group(t('shifts.linesFor', { defaultValue: '{{method}} sales', method: methodLabel(t, 'credit') }), withKind(['credit'])),
    group(t('shifts.linesReturns', { defaultValue: 'Returns' }), rows.filter((r) => r.kind === 'return'), true),
    group(t('shifts.debtPayments', { defaultValue: 'Debt payments' }), rows.filter((r) => r.kind === 'payment')),
  ].join('');

  const title = open
    ? t('shifts.openShiftTitle', { defaultValue: 'Shift in progress' })
    : t('shifts.reportTitle', { defaultValue: 'Register close-out' });

  return `<!doctype html><html dir="${dir}"><head><meta charset="utf-8"/><title>${esc(title)}</title><style>
    @page { size: 80mm auto; margin: 0; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { width: 76mm; margin: 0 auto; padding: 5mm 4mm 7mm; color: ${INK};
      font-family: ${opts.rtl ? "'Segoe UI', Tahoma, Arial, sans-serif" : "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif"};
      font-size: 11.5px; line-height: 1.45; font-variant-numeric: tabular-nums; }
    .c { text-align: center; }
    .store { font-size: 15px; font-weight: 800; }
    .title { font-size: 12.5px; font-weight: 700; margin-top: 1mm; }
    .num { font-size: 11px; font-weight: 700; margin-top: 0.5mm; }
    .meta { font-size: 10.5px; color: ${MUTED}; margin-top: 0.5mm; }
    .sec { margin-top: 3.5mm; padding: 1.2mm 2mm; background: ${INK}; color: #fff; font-weight: 800; font-size: 11.5px;
      border-radius: 1.5mm; display: flex; justify-content: space-between; }
    .row { display: flex; justify-content: space-between; gap: 4mm; margin-top: 1.2mm; }
    .row .lab { color: ${MUTED}; }
    .row .val { font-weight: 600; white-space: nowrap; }
    .row.sub { font-size: 10.5px; margin-top: 0.6mm; }
    .row.sub .lab { padding-inline-start: 3mm; }
    .row.neg .val { font-weight: 700; }
    .row.big { font-size: 13px; font-weight: 800; margin-top: 2mm; }
    .row.big .lab { color: ${INK}; }
    .row.line { border-top: 1px solid ${RULE}; padding-top: 1.2mm; margin-top: 1.6mm; }
    .hint { font-size: 10px; color: ${MUTED}; margin-top: 1mm; padding-inline-start: 3mm; }
    .diff { margin-top: 2mm; padding: 2.5mm 3mm; border-radius: 2mm; display: flex; justify-content: space-between;
      font-weight: 800; font-size: 13px; background: ${PANEL}; }
    .diff.over { border: 1.5px solid ${INK}; }
    .diff.short { border: 1.5px solid ${INK}; background: #fff; }
    .overall { margin-top: 3mm; padding: 2.5mm 3mm; border: 1.5px dashed ${INK}; border-radius: 2mm;
      display: flex; justify-content: space-between; font-weight: 800; font-size: 12px; }
    .gh { display: flex; justify-content: space-between; gap: 3mm; margin-top: 2.5mm; padding-bottom: 0.8mm;
      border-bottom: 1px solid ${RULE}; font-weight: 800; font-size: 11px; }
    .ln { display: flex; justify-content: space-between; gap: 3mm; font-size: 10px; margin-top: 0.6mm; }
    .ln .who { color: ${MUTED}; overflow: hidden; text-overflow: ellipsis; }
    .ln .amt { white-space: nowrap; font-weight: 600; }
    .note { margin-top: 3mm; padding: 2mm 3mm; border: 1px solid ${RULE}; border-radius: 2mm; font-size: 11px; }
    .note .k { font-size: 10px; color: ${MUTED}; font-weight: 700; }
    .sign { margin-top: 9mm; border-top: 1px solid ${RULE}; padding-top: 2mm; font-size: 10px; color: ${MUTED}; text-align: center; }
  </style></head><body>
    <div class="c">
      <div class="store">${esc(opts.storeName)}</div>
      <div class="title">${esc(title)}</div>
      ${m.number ? `<div class="num" dir="ltr">${esc(m.number)}</div>` : ''}
      <div class="meta">${esc(t('shifts.opened', { defaultValue: 'Opened' }))}: ${esc(when(m.openedAt))}</div>
      <div class="meta">${esc(t('shifts.closed', { defaultValue: 'Closed' }))}: ${esc(m.closedAt ? when(m.closedAt) : t('shifts.now', { defaultValue: 'now' }))} · ${esc(duration)}</div>
      ${m.cashier ? `<div class="meta">${esc(t('shifts.cashier', { defaultValue: 'Cashier' }))}: ${esc(m.cashier)}</div>` : ''}
      ${m.device ? `<div class="meta">${esc(m.device)}</div>` : ''}
    </div>

    ${sec(t('shifts.sales', { defaultValue: 'Sales' }), String(m.salesCount))}
    ${salesHtml}
    ${paymentsHtml}
    ${drawersHtml}
    ${overall !== null
      ? `<div class="overall"><span>${esc(t('shifts.overall', { defaultValue: "Overall at today's rate" }))}</span><span dir="ltr">${esc(`${overall > 0 ? '+' : overall < 0 ? '−' : ''}${mc(Math.abs(Math.round(overall)), m.shiftCurrency)}`)}</span></div>`
      : ''}
    ${detailHtml ? sec(t('shifts.detailTitle', { defaultValue: 'Details' })) + detailHtml : ''}
    ${m.note ? `<div class="note"><div class="k">${esc(t('shifts.noteTitle', { defaultValue: "Cashier's note" }))}</div><div>${esc(m.note)}</div></div>` : ''}
    <div class="sign">${esc(t('shifts.signature', { defaultValue: 'Cashier signature' }))}</div>
  </body></html>`;
}
